import { useTranslation } from 'react-i18next';
import { Documentation } from 'graphql-common';
import {
  useEdmDocumentationBulkFavoriteMutation,
  useEdmDocumentationBulkUnfavoriteMutation,
} from '@lib/graphql-common';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import toast from '@lib/components/Toast/Toast';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';

interface DocumentFavoriteCellProps {
  item: Documentation;
  onCompleted: (wasFavorited: boolean) => void;
}

export default function DocumentFavoriteCell({
  item,
  onCompleted,
}: DocumentFavoriteCellProps) {
  const { t } = useTranslation();

  const [bulkFavorite, { loading: favoriteLoading }] =
    useEdmDocumentationBulkFavoriteMutation({
      onError: (error) => showGraphQLErrors(error, t),
    });
  const [bulkUnfavorite, { loading: unfavoriteLoading }] =
    useEdmDocumentationBulkUnfavoriteMutation({
      onError: (error) => showGraphQLErrors(error, t),
    });

  const isLoading = favoriteLoading || unfavoriteLoading;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const wasFavorited = !!item.favorited;
    const mutation = wasFavorited ? bulkUnfavorite : bulkFavorite;
    const successMessage = wasFavorited
      ? t('remove-from-favorites-success')
      : t('add-to-favorites-success');
    mutation({
      variables: { ids: [item.id] },
      update: (cache) => {
        cache.modify({
          id: cache.identify(item),
          fields: {
            favorited: () => !wasFavorited,
          },
        });
      },
      onCompleted: () => {
        toast({ content: successMessage });
        onCompleted(wasFavorited);
      },
    });
  };

  return (
    <IconButton
      icon={item.favorited ? 'bookmark_star' : 'bookmark'}
      symbolsOutlined
      type={
        item.favorited
          ? IconButtonTypes.SecondaryFilled
          : IconButtonTypes.QuaternaryFilled
      }
      filled={item.favorited}
      rounded
      isLoading={isLoading}
      onClick={handleClick}
    />
  );
}
